import { useState } from "react";
import { toast } from "sonner";
import { X, FloppyDisk, Ruler } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";

// Lets a signed-in customer save a sized cut piece — to one of their projects or to a
// personal "saved cuts" list — straight from the Measure popup.
export const SaveCutDialog = ({ item, target, piece, projects = [], onClose, onSaved }) => {
  const [projectId, setProjectId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await portalApi.post("/saved-cuts", {
        slab_id: target?.id || item?.id || "",
        kind: item?.kind || "",
        color: item?.color || "",
        item: target?.item_no || item?.code || "",
        code: item?.code || "",
        length: piece?.length || 0,
        width: piece?.width || 0,
        area: piece?.area || 0,
        note: note.trim(),
        project_id: projectId,
        photo: target?.photo || item?.photo || "",
      });
      toast.success("Cut saved to your portal");
      window.dispatchEvent(new Event("saved-cuts-changed"));
      onSaved?.(data);
      onClose();
    } catch {
      toast.error("Could not save that cut");
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
      data-testid="save-cut-dialog"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Save this cut"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
              <FloppyDisk size={18} weight="bold" className="text-[#c39e6d]" /> Save this cut
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Keep it in your portal to revisit or quote later.
            </p>
          </div>
          <button
            type="button"
            data-testid="save-cut-close"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div
          className="mt-5 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3.5"
          data-testid="save-cut-summary"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#c39e6d]/15 text-[#a9855a]">
            <Ruler size={16} weight="bold" />
          </span>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-slate-900">
              {item?.color || "Slab"}
              {target?.item_no ? ` · item ${target.item_no}` : item?.code ? ` · ${item.code}` : ""}
            </div>
            <div className="text-xs text-slate-500">
              {piece?.length}&quot; × {piece?.width}&quot;
              {piece?.area ? ` · ${piece.area} ft²` : ""}
            </div>
          </div>
        </div>

        <div className="mt-5 space-y-1.5">
          <span className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-slate-500">
            Save to a project (optional)
          </span>
          <select
            data-testid="save-cut-project"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="h-12 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:border-[#c39e6d]"
          >
            <option value="">Just my saved cuts (no project)</option>
            {projects.map((pr) => (
              <option key={pr.id} value={pr.id}>
                {pr.code ? `${pr.code} · ` : ""}
                {pr.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-4 space-y-1.5">
          <span className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-slate-500">
            Note (optional)
          </span>
          <input
            data-testid="save-cut-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. island top, vanity…"
            className="h-12 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:border-[#c39e6d]"
          />
        </div>

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            data-testid="save-cut-cancel"
            onClick={onClose}
            className="h-12 flex-1 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="save-cut-submit"
            onClick={save}
            disabled={busy}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-[#c39e6d] text-sm font-semibold text-white transition-colors hover:bg-[#a9855a] disabled:opacity-40"
          >
            <FloppyDisk size={16} weight="bold" />
            {busy ? "Saving…" : "Save cut"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveCutDialog;
